import { useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { api } from '../api/client'
import type { PickupAnalyticsOut, PickupProductOut } from '../api/types'
import { AXIS, ChartTip, GRID } from '../components/Charts'
import { DateRangeFields, OutletField } from '../components/FilterBar'
import { Kpi } from '../components/Kpi'
import { PageHeader } from '../components/Layout'
import { Panel } from '../components/States'
import { downloadCsv } from '../lib/csv'
import { useFilters } from '../lib/filters'
import { count, formatDate, hourLabel, minutes, percent, shortDate } from '../lib/format'

export function PickupAnalyticsPage() {
  const { filters } = useFilters()

  const query = useQuery({
    queryKey: ['pickup-analytics', filters.from, filters.to, filters.outletId],
    queryFn: () =>
      api.get<PickupAnalyticsOut>('/analytics/pickups', {
        from_date: filters.from,
        to_date: filters.to,
        outlet_id: filters.outletId,
      }),
  })

  const data = query.data

  function exportCsv() {
    if (!data) return
    const lines: (string | number)[][] = [
      [`Pickups ${data.from_date} to ${data.to_date}`],
      [],
      ['Store', 'Requests', 'Collected', 'Typical wait (min)', 'In warehouse (min)', `Within ${data.target_minutes} min %`],
      ...data.stores.map((row) => [
        row.store,
        row.requests,
        row.collected,
        row.wait_median_minutes ?? '',
        row.warehouse_median_minutes ?? '',
        row.within_target_pct ?? '',
      ]),
      [],
      ['Warehouse staff', 'Items dispatched', 'Typical time (min)'],
      ...data.warehouse_staff.map((row) => [row.name, row.items, row.median_minutes ?? '']),
      [],
      ['Product', 'SKU', 'Units', 'Invoices', 'Typical warehouse time (min)', 'Not available'],
      ...data.products.map((row) => [
        row.name,
        row.sku ?? '',
        row.units,
        row.invoices,
        row.warehouse_median_minutes ?? '',
        row.not_available,
      ]),
    ]
    downloadCsv(`Pickups ${data.from_date} to ${data.to_date}.csv`, lines)
  }

  return (
    <>
      <PageHeader
        title="Pickup performance"
        subtitle={`${formatDate(filters.from)} – ${formatDate(filters.to)} · customer pickups, Jamaica time`}
      >
        <button className="btn" onClick={exportCsv} disabled={!data}>
          Export CSV
        </button>
      </PageHeader>

      <div className="content stack">
        <div className="row" style={{ gap: 12, alignItems: 'flex-end' }}>
          <DateRangeFields />
          <OutletField />
        </div>

        <Panel isLoading={query.isPending} error={query.error}>
          {data ? <PickupAnalyticsView data={data} /> : null}
        </Panel>
      </div>
    </>
  )
}

function TimingNote({ data }: { data: PickupAnalyticsOut }) {
  if (data.timing_since === null) {
    return (
      <div className="alert info">
        Timings start once staff use Request from warehouse. Product counts below cover earlier
        pickups too.
      </div>
    )
  }
  if (data.timing_since <= data.from_date) return null
  return (
    <div className="alert info">
      Timings start from {formatDate(data.timing_since)}, the first Request from warehouse. Product
      counts cover the whole period.
    </div>
  )
}

function PickupAnalyticsView({ data }: { data: PickupAnalyticsOut }) {
  const kpis = data.kpis
  const notFinished = kpis.cancelled + kpis.unavailable + kpis.moved_to_delivery + kpis.expired
  const hours = data.hours.map((row) => ({ ...row, label: hourLabel(row.hour) }))
  const reasonMax = Math.max(1, ...data.reasons.map((row) => row.qty))

  return (
    <>
      <TimingNote data={data} />

      <div className="grid cols-4">
        <Kpi
          label="Typical wait"
          value={minutes(kpis.wait_median_minutes)}
          foot={`Request to collected · ${percent(kpis.within_target_pct, 0)} within ${data.target_minutes} min`}
        />
        <Kpi
          label="In the warehouse"
          value={minutes(kpis.warehouse_median_minutes)}
          foot="Request to dispatched"
        />
        <Kpi
          label="At the counter"
          value={minutes(kpis.counter_median_minutes)}
          foot="Dispatched to collected"
        />
        <Kpi
          label="Pickups requested"
          value={count(kpis.requests)}
          foot={`${count(kpis.collected)} collected · ${count(notFinished)} not · ${count(kpis.open_now)} open now`}
        />
      </div>

      <div className="grid cols-2">
        <div className="card">
          <div className="card-head">
            <h2>Requests by day</h2>
          </div>
          <div className="card-body">
            <Panel isLoading={false} error={null} isEmpty={data.daily.length === 0} emptyText="No requests in this period.">
              <div className="chart">
                <ResponsiveContainer>
                  <BarChart data={data.daily} margin={{ top: 6, right: 10, bottom: 0, left: 0 }}>
                    <CartesianGrid {...GRID} vertical={false} />
                    <XAxis dataKey="date" {...AXIS} tickFormatter={shortDate} minTickGap={20} />
                    <YAxis {...AXIS} width={32} allowDecimals={false} />
                    <Tooltip content={<ChartTip />} cursor={{ fill: 'var(--grey-bg)' }} />
                    <Bar dataKey="requests" name="Requests" fill="var(--brand)" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Panel>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Busiest hours</h2>
            <span className="hint">When to have staff ready</span>
          </div>
          <div className="card-body">
            <Panel isLoading={false} error={null} isEmpty={hours.length === 0} emptyText="No requests in this period.">
              <div className="chart">
                <ResponsiveContainer>
                  <BarChart data={hours} margin={{ top: 6, right: 10, bottom: 0, left: 0 }}>
                    <CartesianGrid {...GRID} vertical={false} />
                    <XAxis dataKey="label" {...AXIS} minTickGap={12} />
                    <YAxis {...AXIS} width={32} allowDecimals={false} />
                    <Tooltip content={<ChartTip />} cursor={{ fill: 'var(--grey-bg)' }} />
                    <Bar dataKey="requests" name="Requests" fill="var(--brand)" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Panel>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-head">
          <h2>Stores</h2>
        </div>
        <div className="card-body flush">
          <Panel isLoading={false} error={null} isEmpty={data.stores.length === 0} emptyText="No requests in this period.">
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Store</th>
                    <th className="num">Requests</th>
                    <th className="num">Collected</th>
                    <th className="num">Typical wait</th>
                    <th className="num">In warehouse</th>
                    <th className="num">Within {data.target_minutes} min</th>
                  </tr>
                </thead>
                <tbody>
                  {data.stores.map((row) => (
                    <tr key={row.store}>
                      <td className="strong">{row.store}</td>
                      <td className="num">{count(row.requests)}</td>
                      <td className="num">{count(row.collected)}</td>
                      <td className="num">{minutes(row.wait_median_minutes)}</td>
                      <td className="num">{minutes(row.warehouse_median_minutes)}</td>
                      <td className="num strong">{percent(row.within_target_pct, 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Panel>
        </div>
      </div>

      <div className="grid cols-2">
        <div className="card">
          <div className="card-head">
            <h2>Why pickups didn't finish</h2>
            <span className="hint">Items cancelled or not available</span>
          </div>
          <div className="card-body flush">
            <Panel isLoading={false} error={null} isEmpty={data.reasons.length === 0} emptyText="Nothing cancelled or unavailable in this period.">
              <div className="table-wrap">
                <table>
                  <tbody>
                    {data.reasons.map((row) => (
                      <tr key={row.reason}>
                        <td className="strong">{row.reason}</td>
                        <td style={{ width: '35%' }}>
                          <div className="bar">
                            <span style={{ width: `${(row.qty / reasonMax) * 100}%`, background: 'var(--red)' }} />
                          </div>
                        </td>
                        <td className="num">{count(row.qty)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Warehouse staff</h2>
            <span className="hint">Credited to who dispatched</span>
          </div>
          <div className="card-body flush">
            <Panel isLoading={false} error={null} isEmpty={data.warehouse_staff.length === 0} emptyText="Nothing dispatched in this period.">
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th className="num">Items</th>
                      <th className="num">Typical time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.warehouse_staff.map((row) => (
                      <tr key={row.name}>
                        <td className="strong">{row.name}</td>
                        <td className="num">{count(row.items)}</td>
                        <td className="num">{minutes(row.median_minutes)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          </div>
        </div>
      </div>

      <ProductsCard products={data.products} />
    </>
  )
}

function ProductsCard({ products }: { products: PickupProductOut[] }) {
  const [params] = useSearchParams()

  /** The product's page, found by its HIKE id, as on the Products page. */
  function productHref(row: PickupProductOut): string | null {
    const next = new URLSearchParams(params)
    if (row.product_id) next.set('product', row.product_id)
    else if (row.line_key) next.set('line', row.line_key)
    else return null
    return `/products/${encodeURIComponent(row.sku ?? row.name)}?${next.toString()}`
  }

  return (
    <div className="card">
      <div className="card-head">
        <h2>Products most often picked up</h2>
        <span className="hint">Top {products.length || 50} · excludes returned items</span>
      </div>
      <div className="card-body flush">
        <Panel isLoading={false} error={null} isEmpty={products.length === 0} emptyText="Nothing picked up in this period.">
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Product</th>
                  <th>SKU</th>
                  <th className="num">Units</th>
                  <th className="num">Invoices</th>
                  <th className="num">Typical warehouse time</th>
                  <th className="num">Not available</th>
                </tr>
              </thead>
              <tbody>
                {products.map((row) => {
                  const href = productHref(row)
                  return (
                    <tr key={row.product_id ?? `line:${row.line_key}`}>
                      <td className="strong">{href ? <Link to={href}>{row.name}</Link> : row.name}</td>
                      <td className="cell-sub">{row.sku ?? '—'}</td>
                      <td className="num strong">{count(row.units)}</td>
                      <td className="num">{count(row.invoices)}</td>
                      <td className="num">{minutes(row.warehouse_median_minutes)}</td>
                      <td className="num">{count(row.not_available)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </Panel>
      </div>
    </div>
  )
}
